/**
 * planCompleteness.js — Deterministic scorer that evaluates a generated
 * Assessment & Plan against a curated set of required elements per ICD-10
 * code. Designed for the high-tempo PCM workflow: O(n) regex pass per chip,
 * no API call, no LLM cost, fully transparent reasoning.
 *
 * Returns: { codeKey, label, score (0..1), required: [{name, present}], gaps: [...] }
 *
 * Keep rules conservative — better to under-flag than to nag the provider.
 */

// Each rule: required elements expressed as case-insensitive keyword OR-groups.
// An element is "present" if ANY of its keyword variants appears in the plan text.
// Description is what shows in the gap tooltip ("Missing: Patient education").
const RULES = {
  // --- MUSCULOSKELETAL ---
  'M54.5':  { label: 'Low back pain', required: [
    { name: 'Education',          patterns: ['educat', 'discussed', 'reviewed', 'counsel'] },
    { name: 'Conservative tx',    patterns: ['nsaid', 'ibuprofen', 'naproxen', 'acetaminophen', 'tylenol', 'heat', 'ice', 'rest'] },
    { name: 'PT referral',        patterns: ['physical therapy', '\\bpt\\b', 'pt referral'] },
    { name: 'Red flag screening', patterns: ['red flag', 'cauda equina', 'saddle anesthesia', 'bowel', 'bladder', 'no neuro'] },
    { name: 'Follow-up',          patterns: ['follow-up', 'follow up', 'rtc', 'return to clinic', 'reassess'] },
  ]},
  'M54.50': { label: 'Low back pain, unsp', alias: 'M54.5' },
  'M25.50': { label: 'Knee pain', required: [
    { name: 'Education',          patterns: ['educat', 'discussed', 'reviewed'] },
    { name: 'Imaging consid.',    patterns: ['x-ray', 'xray', 'mri', 'imaging', 'radiograph'] },
    { name: 'Conservative tx',    patterns: ['nsaid', 'rice', 'ice', 'rest', 'compression'] },
    { name: 'PT referral',        patterns: ['physical therapy', '\\bpt\\b'] },
    { name: 'Follow-up',          patterns: ['follow-up', 'follow up', 'rtc'] },
  ]},
  'M25.561': { label: 'Knee pain, R', alias: 'M25.50' },
  'M25.562': { label: 'Knee pain, L', alias: 'M25.50' },
  'M79.3':  { label: 'Myalgia', alias: 'M54.5' },
  'M77.10': { label: 'Lateral epicondylitis', required: [
    { name: 'Activity modification', patterns: ['activity modif', 'avoid', 'rest'] },
    { name: 'NSAID/ice',             patterns: ['nsaid', 'ibuprofen', 'ice'] },
    { name: 'PT/strap',              patterns: ['physical therapy', '\\bpt\\b', 'strap', 'brace'] },
    { name: 'Follow-up',             patterns: ['follow-up', 'follow up', 'rtc'] },
  ]},

  // --- HEADACHE / NEURO ---
  'R51.9':  { label: 'Headache', required: [
    { name: 'Red flag screening', patterns: ['red flag', 'thunderclap', 'worst headache', 'meningismus', 'no focal'] },
    { name: 'Trigger ID',         patterns: ['trigger', 'precipita', 'food diary', 'sleep'] },
    { name: 'Abortive tx',        patterns: ['nsaid', 'ibuprofen', 'triptan', 'sumatriptan', 'tylenol'] },
    { name: 'Follow-up',          patterns: ['follow-up', 'follow up', 'rtc', 'reassess'] },
  ]},
  'G43.909': { label: 'Migraine', alias: 'R51.9' },

  // --- CARDIOVASCULAR ---
  'I10':    { label: 'Hypertension', required: [
    { name: 'BP target',          patterns: ['<130/80', '< 130/80', 'goal bp', 'bp goal', 'blood pressure goal'] },
    { name: 'Lifestyle',          patterns: ['dash', 'sodium', 'exercise', 'lifestyle', 'weight loss'] },
    { name: 'Med plan',           patterns: ['continue', 'titrate', 'increase', 'add', 'lisinopril', 'amlodipine', 'losartan', 'hctz', 'hydrochlorothiazide'] },
    { name: 'Lab follow-up',      patterns: ['bmp', 'electrolyte', 'creatinine', 'lipid', 'cmp'] },
    { name: 'Follow-up',          patterns: ['follow-up', 'follow up', 'rtc', '\\d+\\s*(week|month)'] },
  ]},
  'E78.5':  { label: 'Hyperlipidemia', required: [
    { name: 'Lipid panel',        patterns: ['lipid panel', 'lipid', 'ldl', 'tc', 'cholesterol'] },
    { name: 'Statin/lifestyle',   patterns: ['statin', 'rosuvastatin', 'atorvastatin', 'lifestyle', 'diet'] },
    { name: 'ASCVD risk',         patterns: ['ascvd', 'risk score', 'risk strat', 'cac'] },
    { name: 'Follow-up',          patterns: ['follow-up', 'follow up', 'rtc'] },
  ]},

  // --- ENDOCRINE ---
  'E11.9':  { label: 'Diabetes type 2', required: [
    { name: 'A1c target',         patterns: ['a1c', 'goal a1c', 'a1c <', 'hba1c'] },
    { name: 'Med plan',           patterns: ['metformin', 'insulin', 'continue', 'titrate', 'glp', 'sglt'] },
    { name: 'Foot/eye exam',      patterns: ['foot exam', 'eye exam', 'ophthalmology', 'retina', 'monofilament'] },
    { name: 'Nephropathy screen', patterns: ['microalbumin', 'urine albumin', 'macr', 'egfr'] },
    { name: 'Lifestyle',          patterns: ['diet', 'exercise', 'weight', 'lifestyle'] },
    { name: 'Follow-up',          patterns: ['follow-up', 'follow up', 'rtc', '3 month', 'three month'] },
  ]},

  // --- MENTAL HEALTH ---
  'F41.1':  { label: 'Generalized anxiety', required: [
    { name: 'Severity assess',    patterns: ['gad-?7', 'phq', 'severity', 'symptom score'] },
    { name: 'Therapy referral',   patterns: ['cbt', 'therapy', 'counsel', 'mental health', 'psych'] },
    { name: 'Med consideration',  patterns: ['ssri', 'sertraline', 'escitalopram', 'fluoxetine', 'medication', 'bhcry', 'no med', 'declined med'] },
    { name: 'Safety',             patterns: ['safety', 'suicidal', 'si\\b', 'self-harm', 'no si'] },
    { name: 'Follow-up',          patterns: ['follow-up', 'follow up', 'rtc'] },
  ]},
  'F41.9':  { label: 'Anxiety, unsp', alias: 'F41.1' },
  'F32.9':  { label: 'Depression, unsp', required: [
    { name: 'PHQ-9',              patterns: ['phq-?9', 'depression score'] },
    { name: 'Therapy',            patterns: ['cbt', 'therapy', 'counsel', 'mental health'] },
    { name: 'Med consideration',  patterns: ['ssri', 'sertraline', 'escitalopram', 'medication', 'declined med'] },
    { name: 'Suicide risk',       patterns: ['suicidal', 'si\\b', 'safety', 'no si', 'denies si'] },
    { name: 'Follow-up',          patterns: ['follow-up', 'follow up', 'rtc'] },
  ]},
  'F43.10': { label: 'PTSD, unsp', alias: 'F32.9' },
  'F51.01': { label: 'Insomnia', required: [
    { name: 'Sleep hygiene',      patterns: ['sleep hygiene', 'sleep schedule', 'caffeine', 'screen time'] },
    { name: 'CBT-I or med',       patterns: ['cbt-?i', 'trazodone', 'melatonin', 'sleep stud'] },
    { name: 'Follow-up',          patterns: ['follow-up', 'follow up', 'rtc'] },
  ]},

  // --- RESPIRATORY ---
  'J06.9':  { label: 'Acute URI', required: [
    { name: 'Symptomatic care',   patterns: ['rest', 'fluid', 'hydrat', 'tylenol', 'ibuprofen', 'saline'] },
    { name: 'Antibiotic stewardship', patterns: ['no antibiotic', 'viral', 'no abx', 'antibiotic not'] },
    { name: 'Return precautions', patterns: ['return precaution', 'red flag', 'rtc if', 'worsen', 'fever'] },
  ]},
  'J45.909': { label: 'Asthma, unsp', required: [
    { name: 'Inhaler use',        patterns: ['albuterol', 'rescue', 'inhaler', 'spirometr'] },
    { name: 'Controller',         patterns: ['controller', 'ics', 'fluticasone', 'budesonide'] },
    { name: 'Action plan',        patterns: ['asthma action plan', 'action plan'] },
    { name: 'Follow-up',          patterns: ['follow-up', 'follow up'] },
  ]},

  // --- TBI / CONCUSSION ---
  'S06.0X9A': { label: 'Concussion', required: [
    { name: 'NSI score',          patterns: ['nsi', 'neurobehavioral'] },
    { name: 'PRA stage',          patterns: ['pra stage', 'progressive return', 'stage 1', 'stage 2', 'stage 3', 'stage 4', 'stage 5', 'stage 6'] },
    { name: 'MEDPROS profile',    patterns: ['medpros', 'e-profile', 'duty limit', 'no driving', 'no weapon'] },
    { name: 'Red flags',          patterns: ['red flag', 'er\\b', 'emergency', 'worsening headache', 'vomiting'] },
    { name: 'Follow-up',          patterns: ['2-3 day', '2 to 3 day', 'follow-up', 'reassess'] },
  ]},
  'S06.0X1A': { label: 'Concussion w/ LOC', alias: 'S06.0X9A' },

  // --- WELLNESS ---
  'Z00.00': { label: 'Adult well exam', required: [
    { name: 'USPSTF screening',   patterns: ['uspstf', 'screening', 'mammogram', 'colonoscopy', 'pap', 'aaa', 'low dose ct'] },
    { name: 'Vaccinations',       patterns: ['vaccin', 'flu', 'tdap', 'covid', 'shingles', 'pneumovax'] },
    { name: 'Lifestyle counsel',  patterns: ['exercise', 'diet', 'tobacco', 'alcohol', 'sleep'] },
    { name: 'Follow-up',          patterns: ['follow-up', 'follow up', 'annual', 'rtc'] },
  ]},
  'Z71.2':  { label: 'Imaging review', required: [
    { name: 'Result discussed',   patterns: ['discussed', 'reviewed', 'patient', 'shared'] },
    { name: 'Plan based on',      patterns: ['plan', 'next step', 'follow-up', 'follow up'] },
  ]},
};

/**
 * Coerce an ICD-10 entry into a plain code string. Handles:
 *   - "M54.5" (already a string)
 *   - { code: "M54.5", description: "..." }
 *   - { icd10: "M54.5" }
 *   - { value: "M54.5" }
 *   - any other shape → null (safely skipped)
 */
function _coerceCode(input) {
  if (input == null) return null;
  if (typeof input === 'string') return input;
  if (typeof input === 'object') {
    return input.code || input.icd10 || input.value || input.id || null;
  }
  return null;
}

/**
 * Resolve aliases (e.g., M54.50 → M54.5).
 */
function resolveRule(code) {
  const codeStr = _coerceCode(code);
  if (!codeStr || typeof codeStr !== 'string') return null;
  const upper = codeStr.toUpperCase().trim();
  // Try exact, then dot-stripped, then category prefix (M54.50 → M54.5)
  let rule = RULES[upper];
  if (rule?.alias) rule = RULES[rule.alias];
  if (rule) return { ...rule, codeKey: upper };
  // Last-resort: try collapsing trailing zero (M25.561 → M25.50)
  // Skip for now; conservative approach.
  return null;
}

/**
 * Build a single regex from the keyword list — case-insensitive, word-boundary
 * aware so "ice" doesn't match "service". Patterns containing regex metachars
 * (e.g., '<130/80', '\\d+') are passed through; literal strings are escaped.
 */
function _buildRegex(patterns) {
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const looksLikeRegex = (s) => s.includes('\\') || s.includes('|');
  const parts = patterns.map(p => looksLikeRegex(p) ? p : escape(p));
  return new RegExp(`(${parts.join('|')})`, 'i');
}

/**
 * Score a single ICD-10 against the plan text.
 * Returns null when no rule exists for the code (chip won't render).
 */
export function scoreSingleCode(code, planText) {
  const rule = resolveRule(code);
  if (!rule || !rule.required) return null;
  const text = (planText || '').toLowerCase();
  if (!text || text.length < 20) {
    return {
      codeKey: rule.codeKey,
      label: rule.label,
      score: 0,
      required: rule.required.map(r => ({ name: r.name, present: false })),
      gaps: rule.required.map(r => r.name),
    };
  }
  const checks = rule.required.map((r) => {
    const re = _buildRegex(r.patterns);
    return { name: r.name, present: re.test(text) };
  });
  const presentCount = checks.filter(c => c.present).length;
  return {
    codeKey: rule.codeKey,
    label: rule.label,
    score: presentCount / rule.required.length,
    required: checks,
    gaps: checks.filter(c => !c.present).map(c => c.name),
    presentCount,
    totalCount: rule.required.length,
  };
}

/**
 * Score a list of ICD-10 codes against the plan text. Returns an array of
 * per-code scores (skipping codes with no rule), plus aggregate.
 */
export function scorePlanCompleteness(icd10Codes, planText) {
  const codes = Array.isArray(icd10Codes) ? icd10Codes : [];
  const perCode = codes
    .map(c => scoreSingleCode(c, planText))
    .filter(Boolean);
  if (perCode.length === 0) {
    return { perCode: [], aggregate: null };
  }
  const totalReq = perCode.reduce((s, p) => s + p.totalCount, 0);
  const totalPresent = perCode.reduce((s, p) => s + p.presentCount, 0);
  const aggScore = totalReq > 0 ? totalPresent / totalReq : 0;
  const allGaps = [...new Set(perCode.flatMap(p => p.gaps))];
  return {
    perCode,
    aggregate: {
      score: aggScore,
      presentCount: totalPresent,
      totalCount: totalReq,
      gaps: allGaps,
      coveredCodes: perCode.length,
      uncoveredCodes: codes.length - perCode.length,
      perCode: perCode.map(p => ({ code: p.codeKey, label: p.label, score: p.score })),
    },
  };
}

export default { scorePlanCompleteness, scoreSingleCode };
